import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { getFullName } from '@/utils/getInitials'
import { Dialog, DialogContent, Typography, Box, Divider, Avatar, Card, CardContent, IconButton } from '@mui/material'

import { useNotification } from '@/hooks/useNotification'
import { useTranslator } from '@/hooks/useTranslator'
import { IUser } from '@/@data'
import UserStatusDropDown from '../UserStatusDropDown'
import dayjs from 'dayjs'

interface EmployeeDetailDialogProps {
  open: boolean
  onClose: () => void
  employee: IUser | null
  refetch: () => void
}

export const EmployeeDetailDialog = ({ open, onClose, employee }: EmployeeDetailDialogProps) => {
  const { __ } = useTranslator()
  const n = useNotification()

  const statusBgColor: Record<string, string> = {
    active: '#D4F4E2',
    pending: '#FFECD9',
    waiting: '#FFECD9',
    inactive: '#EAEAEC',
    blocked: '#FFDBDC'
  }

  if (!employee) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      scroll='body'
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', width: '1270px', borderRadius: '32px' }
      }}
    >
      <DialogContent className='p-0 m-0' sx={{ padding: 0, borderRadius: '32px' }}>
        <DialogCloseButton onClick={onClose} disableRipple>
          <i className='tabler-x' />
        </DialogCloseButton>
        <Card
          sx={{
            backgroundColor: statusBgColor[employee.status?.toLowerCase()] || '#F9F9F9'
          }}
        >
          <CardContent>
            <Box className='flex items-star justify-between mb-4'>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Avatar
                  src={employee.avatar_file?.tiny_url || employee.avatar}
                  alt={getFullName(employee)}
                  sx={{ width: 80, height: 80 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Typography className='text-[28px] leading-[42px] font-medium text-[#2f2b3de6]'>
                      {getFullName(employee)}
                    </Typography>
                    <UserStatusDropDown
                      status={employee.status}
                      statusPrevious={employee.status_previous}
                      isDetail={true}
                      sx={{
                        height: '30px',
                        fontSize: '13px'
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <i className='tabler-id text-[18px] text-[#2f2b3d66]' />
                    <span className='text-[15px] leading-[22px] text-[#2f2b3d66]'>{employee?.system_code}</span>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(employee.employee_id || '')
                        n.success(__('Copied to clipboard!'))
                      }}
                    >
                      <i className='tabler-copy text-[#2f2b3db3]' />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Card sx={{ mb: 6, p: 4, borderRadius: 3, backgroundColor: '#fff' }}>
              <Typography className='text-[18px] leading-[28px] font-medium text-[#2f2b3de6]'>
                {__('Personal Info')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <div className='grid gap-5'>
                <div className='grid grid-cols-1 md:grid-cols-4 gap-5'>
                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Name')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {getFullName(employee)}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employer')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.company?.name || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Employee Code')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.employee_id || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Joined Date')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      { employee?.joined_date ? dayjs(employee?.joined_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Contract Type')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.contract_type || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Phone')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.phone_code && `(${employee?.phone_code}) `}
                      {employee?.phone || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Bank Name')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.bank?.shortName || '-'} -{employee?.bank?.name || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Bank Account')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.bank_account_number || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Identity Card')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.identity_card || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Identity Card Issue Date')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.identity_card_issue_date || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Identity Card Issue Place')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.identity_card_issue_place || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Gender')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.gender || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Address')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.address || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('DOB')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      { employee?.birth_date ? dayjs(employee?.birth_date).format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Dependent')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {employee?.dependent || '-'}
                    </Typography>
                  </Box>

                  <Box className='flex flex-col'>
                    <Typography variant='body2' className='text-[#2F2B3D66] text-[15px]'>
                      {__('Tenure')}
                    </Typography>
                    <Typography variant='body2' className='text-[#2F2B3DE5] text-[15px]'>
                      {Number(employee?.tenure) ? Number(employee?.tenure) + ' ' + __('months') : '-'}
                    </Typography>
                  </Box>
                </div>
              </div>
            </Card>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  )
}
